import { Tabs, Tab } from '@nextui-org/react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter
} from '~/components/common/Drawer'
import { Selector, SelectItem } from '~/components/common/Selector'
import { InputArea } from '~/components/common/InputArea'
import { Button } from '~/components/common/Button'
import { type UseRuntimeSettingsResult } from '~/hooks/useRuntimeSettings'
import { useFileUpload } from '~/hooks/useFileUpload'
import { JavaVersion, MavenVersion, MuleRuntimeVersion } from '~/constants/settings'
import { FileUpLoadingSpinner, RemoveIcon, SecuritySearchIcon } from '~/components/icons'
import { XmlFileUploadLeftIcon, XmlFileUploadedLeftIcon, RedDeleteIcon } from '~/constants/icon'
import { BetterToast } from '~/utils/toast'

interface RuntimeSettingsDrawerProps {
  isOpen: boolean
  onOpenChange: () => void
  runtimeSettings: UseRuntimeSettingsResult
}

export function RuntimeSettingsDrawer({
  isOpen,
  onOpenChange,
  runtimeSettings
}: RuntimeSettingsDrawerProps) {
  const muleVersions = Object.values(MuleRuntimeVersion)
  const {
    selectedIndex,
    drawerRuntimeSetting,
    updateDrawerRuntimeSetting,
    handleRuntimeSettingSelectChange,
    handleRemoveSettingsXML
  } = runtimeSettings

  const {
    uploadState,
    securityUploadState,
    handleFileSelect,
    setUploadState,
    setSecurityUploadState
  } = useFileUpload(drawerRuntimeSetting, updateDrawerRuntimeSetting)

  const handleSave = async () => {
    if (!selectedIndex) {
      BetterToast.error('Select a repository before saving runtime settings')
      return
    }

    const success = await handleRuntimeSettingSelectChange(drawerRuntimeSetting)

    if (success) {
      BetterToast.success('Runtime settings saved')
      onOpenChange()
    } else {
      BetterToast.error('Failed to save runtime settings')
    }
  }

  const renderFileUploadSection = (
    isSecurityUpload: boolean,
    state: typeof uploadState,
    setStateFn: typeof setUploadState,
    fileFieldName: keyof typeof drawerRuntimeSetting,
    title: string,
    filename: string,
    icon: JSX.Element
  ) => {
    const inputId = isSecurityUpload ? 'securityFileUpload' : 'fileUpload'
    const hasFile = drawerRuntimeSetting[fileFieldName] as string

    return (
      <div>
        <input
          type='file'
          id={inputId}
          className='hidden'
          onChange={e => handleFileSelect(e, isSecurityUpload)}
          accept='.xml'
        />

        {state.status === 'uploading' ? (
          <div className='w-full h-[74px] px-6 py-4 rounded-[16px] border border-[#EAEAEA] border-dashed bg-[rgba(189_189_189/0.08)] flex items-center gap-x-4 cursor-pointer'>
            <FileUpLoadingSpinner
              clipPathId={isSecurityUpload ? 'clip0_156_3544' : 'clip0_156_3566'}
            />
            <div className='flex-1'>
              <div className='text-[rgba(0,0,0,0.88)] text-base font-medium leading-5 select-none'>
                {title}
              </div>
              <div className='text-[rgba(0,0,0,0.45)] text-sm font-medium leading-[22px] select-none'>
                Uploading ({state.progress}%)
              </div>
            </div>
          </div>
        ) : hasFile ? (
          <div className='group w-full h-[72px] px-6 py-4 rounded-[16px] border border-[#EAEAEA] border-solid bg-[rgba(189_189_189/0.08)] flex items-center justify-between cursor-pointer'>
            <div className='flex items-center gap-4'>
              <div className='w-6 h-6 flex items-center justify-center'>
                <XmlFileUploadedLeftIcon />
              </div>
              <div className='text-[rgba(0,0,0,0.88)] text-base font-medium max-w-[286px] leading-5'>
                {filename}
              </div>
            </div>
            <div
              className='w-[18px] h-[18px] cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity'
              onClick={() => handleRemoveSettingsXML(isSecurityUpload)}
            >
              <RemoveIcon />
            </div>
          </div>
        ) : state.status === 'error' ? (
          <div
            className='w-full h-[74px] px-6 py-4 rounded-[16px] border border-[#EAEAEA] border-dashed bg-[rgba(189_189_189/0.08)] flex items-center justify-between cursor-pointer'
            onClick={() => setStateFn({ status: null })}
          >
            <div className='flex items-center gap-4'>
              <div className='w-6 h-6 flex items-center justify-center'>
                <XmlFileUploadedLeftIcon />
              </div>
              <div className='flex-1'>
                <div className='text-base font-medium leading-[20px] text-[#2E1F36]'>
                  {filename}
                </div>
                <div className='text-[#D94B36] text-[10px] font-mono font-semibold leading-4 select-none'>
                  {state.error}
                </div>
              </div>
            </div>
            <div className='w-[18px] h-[18px] cursor-pointer'>
              <RedDeleteIcon />
            </div>
          </div>
        ) : (
          <label
            htmlFor={inputId}
            className='w-full h-[74px] px-6 py-4 rounded-[16px] border border-[#EAEAEA] border-dashed bg-[rgba(189_189_189/0.08)] flex items-center gap-x-4 cursor-pointer'
          >
            <div className='w-6 h-6 flex items-center justify-center'>{icon}</div>
            <div className='flex-1'>
              <div className='text-[rgba(0,0,0,0.88)] text-base font-medium leading-5 select-none'>
                Upload {title}
              </div>
              <div className='text-[rgba(0,0,0,0.45)] text-sm font-medium leading-[22px] select-none'>
                XML file with maximum size of 5 MB
              </div>
            </div>
          </label>
        )}
      </div>
    )
  }

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      radius='none'
      placement='right'
      classNames={{
        base: 'w-[424px] !max-w-none'
      }}
    >
      <DrawerContent>
        <DrawerHeader className='flex flex-col'>
          <h2 className='text-base font-bold leading-[18px] text-[#262626] max-w-[344px] truncate'>
            {selectedIndex
              ? `${selectedIndex.org_name}/${selectedIndex.repo_name}/${selectedIndex.branch_name}`
              : ''}
          </h2>
        </DrawerHeader>
        <DrawerBody>
          <Tabs
            defaultSelectedKey='presets'
            classNames={{
              tabList: 'w-[376px] h-[43px] bg-[#F5F5F5] rounded-[12px] p-1',
              tab: 'text-[14px] font-medium leading-[20px] h-[35px] data-[selected=true]:bg-white data-[selected=true]:text-[#000] data-[selected=false]:text-[#262626] data-[selected=false]:opacity-38 rounded-[12px] select-none',
              cursor: 'rounded-[12px]',
              tabContent: 'p-0',
              panel: ''
            }}
          >
            <Tab key='uploads' title='Uploads'>
              <div className='pt-6 space-y-8'>
                {/* Settings XML Upload */}
                {renderFileUploadSection(
                  false,
                  uploadState,
                  setUploadState,
                  'settings_xml_raw_txt',
                  'Settings',
                  'Settings.xml',
                  <XmlFileUploadLeftIcon />
                )}

                {/* Security Settings XML Upload */}
                {renderFileUploadSection(
                  true,
                  securityUploadState,
                  setSecurityUploadState,
                  'settings_security_xml_raw_txt',
                  'Security Settings',
                  'Settings-security.xml',
                  <SecuritySearchIcon />
                )}
              </div>
            </Tab>
            <Tab key='presets' title='Presets'>
              <div className='pt-5'>
                {/* Runtime Setting title */}
                <h3 className='text-[rgba(0,0,0,0.65)] text-[13px] font-semibold leading-4 mb-4 font-mono px-2 select-none'>
                  Runtime Setting
                </h3>

                {/* Java Version Select */}
                <div className='mb-6'>
                  <Selector
                    variant='labeled'
                    label='Java Version'
                    placeholder='Select Java Version'
                    className='w-full'
                    optionClassName='w-full'
                    value={drawerRuntimeSetting.java_version}
                    onSelectionChange={selectedValue => {
                      updateDrawerRuntimeSetting(prev => ({
                        ...prev,
                        java_version: selectedValue as JavaVersion
                      }))
                    }}
                  >
                    <SelectItem value='8.0'>Java 8.0</SelectItem>
                    <SelectItem value='11.0'>Java 11.0</SelectItem>
                    <SelectItem value='17.0'>Java 17.0</SelectItem>
                  </Selector>
                </div>

                {/* Maven Version Select */}
                <div className='mb-6'>
                  <Selector
                    variant='labeled'
                    label='Maven Version'
                    placeholder='Select Maven Version'
                    className='w-full'
                    optionClassName='w-full'
                    value={drawerRuntimeSetting.maven_version}
                    onSelectionChange={selectedValue => {
                      updateDrawerRuntimeSetting(prev => ({
                        ...prev,
                        maven_version: selectedValue as MavenVersion
                      }))
                    }}
                  >
                    <SelectItem value='3.5'>Maven 3.5</SelectItem>
                    <SelectItem value='3.8'>Maven 3.8</SelectItem>
                    <SelectItem value='3.9'>Maven 3.9</SelectItem>
                  </Selector>
                </div>

                <div className='mb-6'>
                  <Selector
                    variant='labeled'
                    label='Mule Version'
                    placeholder='Select Mule Version'
                    className='w-full'
                    optionClassName='w-full'
                    value={drawerRuntimeSetting.mule_runtime_version}
                    onSelectionChange={selectedValue => {
                      updateDrawerRuntimeSetting(prev => ({
                        ...prev,
                        mule_runtime_version: selectedValue as MuleRuntimeVersion
                      }))
                    }}
                  >
                    {muleVersions.map(version => (
                      <SelectItem key={version} value={version}>
                        {`Mule ${version}`}
                      </SelectItem>
                    ))}
                  </Selector>
                </div>

                {/* VM Args */}
                <div className='mb-4'>
                  <div className='flex items-center gap-1 mb-4'>
                    <h4 className='text-[rgba(0,0,0,0.65)] text-[13px] font-semibold leading-4 font-mono px-2 select-none'>
                      VM Arg
                    </h4>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                    >
                      <path
                        d='M8 1C4.13438 1 1 4.13438 1 8C1 11.8656 4.13438 15 8 15C11.8656 15 15 11.8656 15 8C15 4.13438 11.8656 1 8 1ZM8 13.8125C4.79063 13.8125 2.1875 11.2094 2.1875 8C2.1875 4.79063 4.79063 2.1875 8 2.1875C11.2094 2.1875 13.8125 4.79063 13.8125 8C13.8125 11.2094 11.2094 13.8125 8 13.8125Z'
                        fill='black'
                        fillOpacity='0.45'
                      />
                      <path
                        d='M7.25 5.25C7.25 5.44891 7.32902 5.63968 7.46967 5.78033C7.61032 5.92098 7.80109 6 8 6C8.19891 6 8.38968 5.92098 8.53033 5.78033C8.67098 5.63968 8.75 5.44891 8.75 5.25C8.75 5.05109 8.67098 4.86032 8.53033 4.71967C8.38968 4.57902 8.19891 4.5 8 4.5C7.80109 4.5 7.61032 4.57902 7.46967 4.71967C7.32902 4.86032 7.25 5.05109 7.25 5.25ZM8.375 7H7.625C7.55625 7 7.5 7.05625 7.5 7.125V11.375C7.5 11.4438 7.55625 11.5 7.625 11.5H8.375C8.44375 11.5 8.5 11.4438 8.5 11.375V7.125C8.5 7.05625 8.44375 7 8.375 7Z'
                        fill='black'
                        fillOpacity='0.45'
                      />
                    </svg>
                  </div>
                  <InputArea
                    placeholder='Write VM Arg'
                    size='large'
                    value={drawerRuntimeSetting.vm_args || ''}
                    onChange={e => {
                      updateDrawerRuntimeSetting(prev => ({
                        ...prev,
                        vm_args: e.target.value
                      }))
                    }}
                    className='w-full min-h-[127px]'
                    rows={6}
                  />
                </div>
              </div>
            </Tab>
          </Tabs>
        </DrawerBody>
        <DrawerFooter className='flex justify-center'>
          <Button className='w-[196px] h-9 select-none' onClick={handleSave}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
